import cn from 'classnames';
import IconArrowLeft from 'assets/icons/arrow-left.svg';
import IconArrowUp from 'assets/icons/arrow-right-alt.svg';
import IconCircleCross from 'modules/calorie-calculator/wizard/illustrations/cross.svg';
import IllustrationBowl from 'assets/icons/food-bowl.svg';
import Button from 'components/button-new';
import Image from 'next/image';
import { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { PickyCatsQuestion } from 'modules/picky-cats/types';

interface QuestionProps {
  question: PickyCatsQuestion;
  currentIndex: number;
  totalSteps: number;
  selectedOptionId?: string;
  onSelect: (optionId: string) => void;
  onBack?: () => void;
  onNext: () => void;
}

/** Picky Cats single-question screen with single-select rows and a step progress bar. */
const Question: FC<QuestionProps> = ({
  question,
  currentIndex,
  totalSteps,
  selectedOptionId,
  onSelect,
  onBack,
  onNext,
}) => {
  const progress = totalSteps > 0 ? Math.round(((currentIndex + 1) / totalSteps) * 100) : 0;

  return (
    <div className="flex flex-col gap-6 px-4 pt-4 pb-8" data-test="picky-cats-question">
      <div className="flex flex-col items-center gap-6 pt-12">
        <div
          className="relative flex h-[100px] w-[98px] items-center justify-center"
          data-test="picky-cats-question-illustration"
        >
          {question.illustrationSrc ? (
            <Image src={question.illustrationSrc} alt="" fill className="object-contain" />
          ) : (
            <IllustrationBowl className="h-auto w-full" />
          )}
        </div>
        <p
          className="mindset-28 text-text-light-bg-primary text-center"
          data-test="picky-cats-question-prompt"
        >
          {question.prompt}
        </p>
      </div>

      <div className="flex flex-col gap-3 pt-4">
        {question.options.map((option) => {
          const isSelected = option.id === selectedOptionId;

          return (
            <button
              key={option.id}
              type="button"
              className={cn(
                'flex w-full items-center gap-3 rounded-2xl py-3 pr-3 pl-4 text-left',
                isSelected ? 'bg-brand-blue-50' : 'bg-background-default'
              )}
              onClick={() => onSelect(option.id)}
              aria-pressed={isSelected}
              data-test={`picky-cats-question-option-${option.id}`}
            >
              <IconCircleCross
                className={cn(
                  'size-10 shrink-0',
                  isSelected ? 'text-brand-blue-200' : 'text-[#B3B8C1]'
                )}
              />
              <span className="text-text-light-bg-primary grow text-xl leading-normal">
                {option.label}
              </span>
            </button>
          );
        })}
      </div>

      {question.helperCopy && (
        <div
          className="flex flex-col items-center gap-2 text-center"
          data-test="picky-cats-question-helper"
        >
          <IconArrowUp className="text-text-light-bg-secondary size-6 -rotate-90" />
          <p
            className="text-text-light-bg-secondary text-sm"
            data-test="picky-cats-question-helper-text"
          >
            {question.helperCopy}
          </p>
        </div>
      )}

      <div
        className="bg-background-default h-1.5 w-full overflow-hidden rounded-sm"
        data-test="picky-cats-question-progress"
      >
        <div className="bg-primary-main h-full rounded-sm" style={{ width: `${progress}%` }} />
      </div>

      <div className="flex items-center gap-5">
        {onBack && (
          <FormattedMessage id="picky-cats:a11y:back" defaultMessage="Back">
            {(label) => (
              <Button
                layout="outline"
                size="large"
                aria-label={String(label)}
                className="aspect-square w-fit!"
                onClick={onBack}
                data-test="picky-cats-question-back"
              >
                <IconArrowLeft className="size-6" />
              </Button>
            )}
          </FormattedMessage>
        )}
        <Button
          size="large"
          iconRight
          className="w-full"
          onClick={onNext}
          data-test="picky-cats-question-next"
        >
          <FormattedMessage id="picky-cats:cta:next" defaultMessage="Continue" />
        </Button>
      </div>
    </div>
  );
};

export default Question;
